import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Link, Divider, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ChangelogProps {
  open: boolean;
  onClose: () => void;
}

const Changelog: React.FC<ChangelogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const dialogBg = theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff';
  const textColor = theme.palette.text.primary;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 3, pb: 1, fontWeight: 800, color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: dialogBg }}>
        Changelog
        <IconButton onClick={onClose} sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, md: 4 }, background: dialogBg, color: textColor }}>
        <Typography sx={{ fontSize: 16, color: textColor, mb: 3 }}>
          All notable changes to the AutoUpgrade Composer will be documented in this file.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.5-1] - 2026-08-13 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added <b>GOLDIMAGE</b> patch mode for Deploy and Create Home execution modes — applies a user-supplied local Gold Image <code>.zip</code> (e.g. <code>Gold_Image.zip</code>) instead of downloading patches individually. Generates <code>GOLDIMAGE:&lt;path&gt;/my_image_&lt;version&gt;.zip</code> in the patch parameter. Exclusive: enabling GOLDIMAGE disables all other patch checkboxes and the Download toggle. Requires AutoUpgrade 26.5+.</li>
          <li>When GOLDIMAGE is active: <code>download=NO</code> is forced in the config, the Download toggle is shown as OFF and disabled, the Keystore Path field is hidden, and <code>global.keystore</code> is omitted from the generated config</li>
          <li>Added <b>RECOMMENDED:X.Y</b> version-pinned syntax — when RECOMMENDED is checked, an optional inline version field appears. Accepts <code>19.x / 21.x / 23.x / 26.x</code> format and validates it in Download and Create Home modes. Generates <code>RECOMMENDED:X.Y</code> in the patch parameter when a version is specified</li>
          <li>Added <b>Create Gold Image</b> option for Deploy and Create Home modes — generates <code>create_gold_image=YES</code> (default template) or a custom filename built interactively with placeholder chips (<code>%RELEASE%</code>, <code>%UPDATE%</code>, <code>%DATE%</code>, <code>%TIMESTAMP%</code>). The <code>.zip</code> extension is always enforced. Requires AutoUpgrade 26.5+.</li>
          <li>Added <b>gold_image.security_patch_level</b> filter for Gold Image downloads — visible when <code>gold_image=YES</code> or <code>ALL</code>; options are CRITICAL, HIGH (AU default), MEDIUM, LOW. Only written to config when a non-default level is selected. Valid for Download mode and Deploy/Analyze with <code>download=YES</code>. Requires AutoUpgrade 26.5+.</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.4-3] - 2026-07-30 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added <b>Existing Target Home</b> mode for Analyze and Deploy execution modes — when enabled, AutoUpgrade will not download or create a new Oracle Home; instead, it moves the database to the pre-patched Target Home and runs Datapatch to finalize the patching operation. The DBA must ensure the Target Home already contains all desired patches (RU, OJVM, one-offs, etc.) before running</li>
          <li>When Existing Target Home is active: <code>folder</code>, <code>patch</code>, <code>download</code>, <code>target_version</code>, and <code>platform</code> parameters are automatically removed from the generated config; the <code>-patch</code> flag is also removed from the execution command</li>
          <li>Home Settings, Patch Content, and Download Settings tabs are disabled (with tooltip explanation) when Existing Target Home mode is on</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.4-2] - 2026-07-06 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed Target Home placeholder in Patch Content not reading from <code>versionConfig.json</code> — the UI now correctly reflects the latest RU versions (19.31, 21.22, 23.26.2) loaded from the external config file, instead of hardcoded stale values</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.4-1] - 2026-07-03 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added <b>CSPU</b> (Cumulative Security Patch Update) placeholder checkbox in Patch Content — disabled/grayed out with contextual tooltip explaining availability per platform and Oracle version (19c non-Linux: coming soon; 21c Linux/Windows: coming soon)</li>
          <li>Removed <b>add -csi</b> step from the Initial Setup for Downloads instructions — CSI is no longer required for keystore configuration</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.3-7] - 2026-06-23 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.warning.main }}>
          Improvements
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added a warning note in the <b>Initial Setup for Downloads</b> section clarifying that <b>OCI accounts are not supported</b> — only standard My Oracle Support (MOS) accounts can be used to download patches</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.3-6] - 2026-06-17 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added <b>Latest AutoUpgrade</b> release card — fetches live version, release date, file size, SHA-256 checksum, and download URL directly from Oracle's public JSON feed. Includes a <code>wget</code> command with one-click copy and a direct download button. Data is cached for 1 hour and always shows a fallback when offline.</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.3-5] - 2026-06-05 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Re-enabled <b>SQLcl</b> download option in Patch Content — now fully available as of AutoUpgrade 26.4. Added tooltip describing SQLcl as SQL Developer Command Line</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.3-4] - 2026-05-29 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed <code>target_pdb_copy_option</code> parameter for <b>NonCDB to CDB</b> conversion — the SID was incorrectly appended to the parameter name (e.g. <code>.TST1</code>). The parameter is now generated correctly as <code>upg1.target_pdb_copy_option=file_name_convert=...</code></li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.3-3] - 2026-05-19 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Release Update version placeholders (used in target and source home paths) are now managed through an external configuration file, allowing version values to be updated independently of application releases</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.3-2] - 2026-04-08 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Reverted patch folder parameter back to <code>folder</code> (from <code>download_folder</code>) for backward compatibility — ensures configs work correctly with AutoUpgrade versions prior to 26.3</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.3-1] - 2026-04-06 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added <b>AHF</b>, <b>CVU</b>, <b>SQLcl</b>, and <b>TOOLS</b> options to Patch Content as per AUPG-4970. Selecting <b>TOOLS</b> is equivalent to specifying AU, OPATCH, AHF, CVU, and SQLcl combined — individual options are automatically checked and disabled when TOOLS is selected. <b>SQLcl</b> is now fully available as of AutoUpgrade version 26.4</li>
          <li>Reordered patch options in Patch Content for better readability: <b>RU, MRP, DPBP, JDK, OJVM, OCW, SDO</b> (left column) and <b>TEXT, OPATCH, AU, AHF, CVU, SQLcl</b> (right column)</li>
        </ul>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Renamed patch parameter <code>folder</code> to <code>download_folder</code> as per AUPG-5133 — the previous parameter name has been deprecated in AutoUpgrade 26.3. Configs loaded with the old <code>folder</code> parameter will still be parsed correctly for backwards compatibility</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-8] - 2026-03-31 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li><b>Proactive Fixups</b> (<code>tune_setting=proactive_fixups=true</code>) is no longer enabled by default — it must now be explicitly opted into via the Additional Options toggle, as it is an opt-in performance tuning feature and not applicable to all upgrade scenarios</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-7] - 2026-03-25 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added <b>Load / Validate Configuration</b> — load any AutoUpgrade config file, whether generated by the Composer or written manually, and instantly validate it. The validator parses every parameter against a comprehensive reference list extracted directly from <code>autoupgrade.jar</code> and the official AutoUpgrade documentation: unknown or misspelled parameters are flagged as errors, suspicious values as warnings, and parameters valid but not yet exposed in the Composer UI are listed as informational notes. Typo detection is built-in — if a parameter name is close to a known one, the validator suggests the correct spelling (e.g. <i>"Did you mean patch1.after_action.deploy?"</i>). Upgrade type, database count, and overall validation status are shown at a glance via color-coded indicators</li>
          <li>Added <b>Unplug-Plug</b> upgrade type — unplugs a PDB from the source CDB and plugs it into a target CDB during upgrade. Configures Source PDB, Target CDB, File Name Convert (NONE default / Use Source-Target Paths), and Manage Standbys Clause with an Enabled/Deferrable Recovery toggle and NONE / ALL / ALL EXCEPT / Specific Standbys options</li>
        </ul>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added <b>Parallel PDB Creation</b> field to PDB Relocation Settings — controls the number of parallel processes used by the CDB to make the initial clone copy (<code>parallel_pdb_creation_clause.PDB</code>)</li>
          <li>Added <b>Drop DB Link</b> toggle to PDB Relocation / Refreshable Clone Settings — instructs AutoUpgrade to drop the database link after the upgrade is complete (<code>drop_dblink=yes</code>)</li>
          <li>All upgrade mode settings sections (CDB Conversion, PDB Relocation, Unplug-Plug) now use a consistent bordered section style matching the patch section layout</li>
          <li>PDB Relocation refresh interval field redesigned for better readability</li>
          <li>Validation dialog and Load Configuration modal now correctly adapt to the light theme</li>
          <li>Removed <b>Custom TNS Admin</b> from Patching Additional Options — <code>source_tns_admin_dir</code> and <code>target_tns_admin_dir</code> are upgrade-only parameters and are not applicable to patching operations</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-6] - 2026-03-12 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Patch Content optional version placeholders now reflect the selected target version across all execution modes: <b>19.30</b> for Oracle 19c, <b>21.20</b> for Oracle 21c, and <b>23.26.1</b> for Oracle 23c/26ai</li>
          <li><b>OJVM</b> option in Patch Content is now disabled when target version is 21, 23, or 26 — OJVM is already included in the Release Update for those versions</li>
          <li><b>SDO (SDOBP)</b> option in Patch Content is temporarily disabled for 21c/23c/26ai — will be released on AutoUpgrade version 26.4</li>
          <li><b>TEXT</b> option in Patch Content is now disabled when target version is 21, 23, or 26 — Oracle Text content is fully embedded in the Release Update starting on 21c and no separate TEXT patch is available for those versions. For Oracle 19c, a separate TEXT MLR can still be applied on top of the Release Update</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-5] - 2026-03-03 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed an issue where <b>Standard Edition 2 (SE2)</b> was incorrectly available as an edition option when Oracle 26ai or Oracle 23c was selected as the target version. Since SE2 is not available in Oracle 23c and later, the option is now properly disabled when target version 23 or 26 is selected</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-4] - 2026-02-26 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added a new <b>Home Settings</b> tab for Analyze, Fixups, Deploy, and Create Home execution modes, exposing Oracle home installation parameters previously not available in the Composer. The tab includes: Oracle Home User (Windows account type), Oracle Base, Edition, Inventory Location, Inventory Group, Home Name, OPatch Conflict Handling, Read-Only Home, Apply RU (ru_apply), TMP Directory (env=TMP), and OS Groups (OSDBA, OSOPER, OSBACKUPDBA, OSDGDBA, OSKMDBA, OSRACDBA)</li>
          <li>Added <b>Binary Options</b> section within Home Settings with tri-state control (ON / OFF / UNSET) for each option: RAC, ASM, dNFS, Data Mining, JOX, OLAP, Partitioning, RAT, SDO, and Unified Auditing. UNSET follows Oracle installation defaults (most options enabled by default); ON or OFF explicitly writes the parameter to the generated config</li>
          <li>Cluster Nodes field is now shown only when RAC is explicitly set to ON</li>
          <li>Home Settings tab is positioned as the second tab for quick access, immediately after Basic Information</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-3] - 2026-02-11 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added AHF, CVU, and TOOLS options to Patch Content. Selecting AHF appends AHF, CVU appends CVU, and TOOLS appends TOOLS (equivalent to AU+OPATCH+AHF+CVU combined) to the patch parameter. OPATCH and AU have moved to the new Tools section - as per enhancement AUPG-4970</li>
          <li>Added the capability to automatically update the autoupgrade.jar file for customers based on their preference (AU) - as per enhancement AUPG-4948</li>
          <li>Added the option for SDO and TEXT in Patch Content. When SDO is selected, the patch parameter is appended with SDOBP; when TEXT is selected, the patch parameter is appended with TEXT - as per enhancement AUPG-4710</li>
          <li>Allow 26 as alias for 23 in target_version for create_home and download modes - as per enhancement AUPG-5117</li>
        </ul>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>OCW remains active and clickable when RECOMMENDED is selected, allowing users to optionally add OCW to the patch configuration without it being auto-checked</li>
          <li>Visual enhancements: two-column layout for Patch Content options, tooltips with descriptive text for each patch type (RU, OJVM, OPATCH, DPBP, AU, JDK, OCW, SDO, TEXT, MRP), and capitalized display for dropdown options (RAC Rolling, Gold Image, Drain Timeout)</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-2] - 2026-02-10 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added RAC Rolling parameter for Deploy and Analyze modes with options: DISABLED, REQUIRED, FORCE, and AUTO (default). RAC Rolling enables patching RAC databases without downtime by patching nodes one at a time while keeping the database available on other nodes - as per enhancement AUPG-4157, released on AutoUpgrade v26.1/v26.2</li>
          <li>Added Drain Timeout parameter for Deploy and Analyze modes, allowing configuration of how long AutoUpgrade should wait for sessions to drain before stopping an instance. Supports either a time value in seconds (integer greater than or equal to 0) or WAIT mode, which gives full control to the user to proceed when ready via command-line prompt - as per enhancement AUPG-4157, released on AutoUpgrade v26.1/v26.2</li>
        </ul>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added Ignore PreReq Failure switch for Create Home, Analyze, and Deploy modes. When enabled, AutoUpgrade will ignore prerequisite check failures during patching operations. This is particularly useful when a new Oracle Home is being installed or created, allowing the process to continue despite failed prerequisite checks</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [26.2-1] - 2026-02-09 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added Gold Image field in Download mode with options: NO, YES, ALL, and AUTO (default). The Gold Image feature allows AutoUpgrade to download pre-built Oracle Home images from Oracle Updater Service, significantly reducing download and setup time for patch operations - as per enhancement AUPG-4908, released on AutoUpgrade v26.1/v26.2</li>
          <li>Enhanced Initial Setup for Downloads section with detailed instructions for configuring keystore with MOS credentials and CSI (Customer Support Identifier), including step-by-step guidance for adding CSI to the keystore - as per enhancement AUPG-4908, released on AutoUpgrade v26.1/v26.2</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-5] - 2026-02-02 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Updated placeholders to use 23.26.1, 21.20, and 19.30 for Download and Create Home modes</li>
          <li>Changed placeholder to 19.30 for target_home</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-4] - 2026-01-30 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Changed the default path for log Oracle Home 23.26 to 23.26.1</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-3] - 2026-01-24 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Changes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Changed the default path for log directory</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-2] - 2025-12-26 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
          Visual Enhancements
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Overall visual enhancements for better readability</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.6-1] - 2025-10-21 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added the option to select JDK in the Patch Content as per enhancement AUPG-4836, released on AutoUpgrade v25.5</li>
          <li>Added the validation to disallow keystore and global_log_dir to use the same path as per enhancement AUPG-5007, released on AutoUpgrade v25.5</li>
          <li>Added the support the values for 21 and 23 on target_version, as per enhancement AUPG-5018, released on AutoUpgrade v25.5</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.4-3] - 2025-08-27 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added the option to use a dynamic path with %RELEASE%.%UPDATE% for AutoUpgrade Patching, allowing automatic path generation based on source home or default patterns</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.4-2] - 2025-08-05 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed an issue that was consuming excessive CPU resources in PatchSection due to infinite re-renders</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.4-1] - 2025-08-04 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Added the option to specify the custom Oracle Clusterware patch level (OCW:X.Y) on AutoUpgrade Patching as per enhancement AUPG-4748 released on AutoUpgrade version v25.4, released by Oracle on 2025-07-30</li>
          <li>Added the option to specify the cluster_nodes on AutoUpgrade Patching as per enhancement AUPG-4440 released on AutoUpgrade version v25.4, released by Oracle on 2025-07-30</li>
          <li>Added the option to enable the Binary Option for RAC on AutoUpgrade Patching (feature already exists on AutoUpgrade but added on AutoUpgrade-Composer to be used along with cluster_nodes if needed)</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-3] - 2025-06-24 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          New Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Two new parameters added for Create Home Mode:
            <ul style={{ marginTop: 4, marginBottom: 4, marginLeft: 24, color: textColor }}>
              <li>oraInventory</li>
              <li>Inventory Group</li>
            </ul>
          </li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-2] - 2025-06-02 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}>
          Bug Fixes
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Fixed a bug on Start Time</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-1] - 2025-06-02 (Stable)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          Major Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>First STABLE release with full Upgrade capability</li>
          <li>Upgrade modes supported:
            <ul style={{ marginTop: 4, marginBottom: 4, marginLeft: 24, color: textColor }}>
              <li>Regular upgrade</li>
              <li>Non-CDB to CDB (noncdb2cdb)</li>
              <li>PDB Relocation (pdbrelocation)</li>

            </ul>
          </li>
          <li>All previous patching and configuration features from Beta</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: textColor }}>
          [25.3-b45] - 2025-05-13 (Beta)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}>
          Features
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 16, fontSize: 16, color: textColor }}>
          <li>Database configuration generator for Oracle AutoUpgrade</li>
          <li>Support for patch operations</li>
          <li>Multiple database configurations</li>
          <li>Tabbed interface for patch configurations</li>
          <li>Context-aware subtabs for different operations</li>
          <li>Proper file naming for config files based on SID</li>
          <li>Dark/Light theme with system preference detection</li>
          <li>Configuration file download</li>
          <li>Run command generation with multiple execution modes:
            <ul style={{ marginTop: 4, marginBottom: 4, marginLeft: 24, color: textColor }}>
              <li>Analyze Only</li>
              <li>Fixups</li>
              <li>Deploy (default)</li>
              <li>Download</li>
              <li>Create Home</li>
            </ul>
          </li>
          <li>Tooltips for various fields and switches for better user guidance.</li>
          <li>Enhanced config loading: loaded configs now set execution mode, download switch, and all patch fields correctly.</li>
          <li>Platform-specific configuration: Windows-specific fields and switches are now handled and displayed properly.</li>
          <li>UI/UX improvements: modernized layout, better spacing, and clearer section headers.</li>
          <li>Edition, Oracle Base, and Target Version switches are auto-enabled when present in loaded configs.</li>
        </ul>
        <Divider sx={{ my: 3 }} />
        <Typography sx={{ fontSize: 16, color: textColor, mb: 2 }}>
          Built with <b>React</b> and <b>Material UI</b> for a modern, responsive experience.<br />
          For more information, visit{' '}
          <Link href="https://viniciusdba.com.br" target="_blank" rel="noopener" underline="hover">
            https://viniciusdba.com.br
          </Link>
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default Changelog; 